"use client";

import { useSyncExternalStore, useCallback, useMemo } from "react";
import { selectionStore, SelectionType, SelectionUpdateInput } from "./SelectionStore";

/**
 * Hook for checking if a specific item is selected.
 * Uses useSyncExternalStore for fine-grained subscriptions - only re-renders
 * when this specific item's selection state changes.
 */
export function useIsItemSelected(type: SelectionType, itemId: string): boolean {
  const subscribe = useCallback(
    (callback: () => void) => selectionStore.subscribeItem(type, itemId, callback),
    [type, itemId]
  );

  const getSnapshot = useCallback(() => selectionStore.isSelected(type, itemId), [type, itemId]);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Hook for getting the full selection array for a type.
 * Will re-render on any selection change for that type.
 * Use sparingly - prefer useIsItemSelected for individual items.
 */
export function useSelection(type: SelectionType): string[] {
  const subscribe = useCallback(
    (callback: () => void) => selectionStore.subscribeGlobal(callback),
    []
  );

  const getSnapshot = useCallback(() => selectionStore.getSelection(type), [type]);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Hook for getting selection methods without subscribing to changes.
 * Safe to call frequently as it doesn't cause re-renders.
 * Returns stable function references that won't change between renders.
 */
export function useSelectionActions() {
  return useMemo(
    () => ({
      setSelectedItems: (input: SelectionUpdateInput) => selectionStore.selectItems(input),
      addItems: (input: SelectionUpdateInput) => selectionStore.addItems(input),
      deselectItems: (input: SelectionUpdateInput) => selectionStore.deselectItems(input),
      clearSelection: (input: SelectionUpdateInput) => selectionStore.clearSelection(input),
      isSelected: (type: SelectionType, itemId: string) => selectionStore.isSelected(type, itemId),
      getSelection: (type: SelectionType) => selectionStore.getSelection(type),
      getSelectionSet: (type: SelectionType) => selectionStore.getSelectionSet(type),
      registerEditHandler: (type: SelectionType, handler: (itemId: string) => void) =>
        selectionStore.registerEditHandler(type, handler),
      getEditHandler: (type: SelectionType) => selectionStore.getEditHandler(type),
      unregisterEditHandler: (type: SelectionType) => selectionStore.unregisterEditHandler(type),
    }),
    []
  );
}

/**
 * Combined hook that provides both the selection state and actions for a type.
 * Will re-render on any selection change for that type.
 */
export function useSelectionContext(type: SelectionType) {
  const selection = useSelection(type);
  const actions = useSelectionActions();

  return {
    selectedItems: selection,
    ...actions,
  };
}
